import {
  Box,
  Center,
  Divider,
  Heading,
  HStack,
  SimpleGrid,
  Text,
  Container,
  Button,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  Image,
} from "@chakra-ui/react";

import BackgroundImage from "../images/background-image.webp";

import { useEffect, useState } from "react";
import { merge, tap } from "rxjs";
import { RoyaltiesInfo } from "../core/RoyaltiesInfo";
import Web3Service from "../core/web3.service";

// TODO: MOVE
function nFormatter(num: number, digits: number) {
  const lookup = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "G" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "P" },
    { value: 1e18, symbol: "E" },
  ];

  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  var item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return num >= item.value;
    });
  return item
    ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol
    : num.toFixed(2);
}

interface RoyaltiesClaimerProps {}

const RoyaltiesClaimer: React.FC<RoyaltiesClaimerProps> = (props) => {
  const web3Service = Web3Service.shared();

  const [account, setAccount] = useState<string | undefined>(undefined);
  const [royalties, setRoyalties] = useState<RoyaltiesInfo | undefined>(
    undefined
  );
  const [royaltiesDao, setRoyaltiesDao] = useState<RoyaltiesInfo | undefined>(
    undefined
  );
  const [royaltiesExp, setRoyaltiesExp] = useState<RoyaltiesInfo | undefined>(
    undefined
  );
  const [royaltiesMutants, setRoyaltiesMutants] = useState<
    RoyaltiesInfo | undefined
  >(undefined);
  const [royaltiesUltimate, setRoyaltiesUltimate] = useState<
    RoyaltiesInfo | undefined
  >(undefined);

  useEffect(() => {
    web3Service.getLabsRoyalties();
    web3Service.getDrDaoRoyalties();
    web3Service.getExpRoyalties();
    web3Service.getMutantsRoyalties();
    web3Service.getUltimateRoyalties();

    const account$ = web3Service.account$.pipe(
      tap((account) => {
        setAccount(account);
      })
    );

    const royalties$ = web3Service.royalties$.pipe(
      tap((royalties) => {
        setRoyalties(royalties);
      })
    );

    const royaltiesDao$ = web3Service.royaltiesDao$.pipe(
      tap((royaltiesDao) => {
        setRoyaltiesDao(royaltiesDao);
      })
    );

    const royaltiesExp$ = web3Service.royaltiesExp$.pipe(
      tap((royaltiesExp) => {
        setRoyaltiesExp(royaltiesExp);
      })
    );

    const royaltiesMutants$ = web3Service.royaltiesMutants$.pipe(
      tap((royaltiesMutants) => {
        setRoyaltiesMutants(royaltiesMutants);
      })
    );

    const royaltiesUltimate$ = web3Service.royaltiesUltimate$.pipe(
      tap((royaltiesUltimate) => {
        setRoyaltiesUltimate(royaltiesUltimate);
      })
    );

    const subscription = merge(
      royalties$,
      royaltiesDao$,
      royaltiesExp$,
      royaltiesMutants$,
      royaltiesUltimate$,
      account$
    ).subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [account]);

  return (
    <>
      <Box
        position={"fixed"}
        backgroundImage={`url(${BackgroundImage})`}
        backgroundSize="cover"
        width={"100%"}
        height={"100%"}
        opacity={0.5}
        zIndex={-3}
      />

      <Center>
        <Box>
          <Center>
            <Box maxWidth={480}>
              <Box textAlign={"center"}>
                <Text
                  fontSize={"3xl"}
                  mt={8}
                  mb={16}
                  px={6}
                  borderRadius={24}
                  bgColor="black"
                >
                  Royalties
                </Text>
              </Box>
            </Box>
          </Center>

          {account &&
            RoyaltiesSection(
              "Ultimate Mutants",
              `${royaltiesUltimate?.availableToClaim ?? "-"} CRO`,
              royaltiesUltimate?.totalRoyalties ?? 0,
              web3Service.claimAllUltimateRoyalties
            )}
          {account &&
            RoyaltiesSection(
              "Dr Dao X Mutant",
              `${royaltiesMutants?.availableToClaim ?? "-"} CRO`,
              royaltiesMutants?.totalRoyalties ?? 0,
              web3Service.claimAllMutantsRoyalties
            )}
          {account &&
            RoyaltiesSection(
              "AI Experiments",
              `${royaltiesExp?.availableToClaim ?? "-"} CRO`,
              royaltiesExp?.totalRoyalties ?? 0,
              web3Service.claimAllExpRoyalties
            )}
          {account &&
            RoyaltiesSection(
              "AI Labs",
              `${royalties?.availableToClaim ?? "-"} CRO`,
              royalties?.totalRoyalties ?? 0,
              web3Service.claimAllLabsRoyalties
            )}
          {account &&
            RoyaltiesSection(
              "Dr Dao",
              `${royaltiesDao?.availableToClaim ?? "-"} CRO`,
              royaltiesDao?.totalRoyalties ?? 0,
              web3Service.claimAllDrDaoRoyalties
            )}
          {!account && (
            <Container>
              <Center mt={8}>
                <Text maxW={"2xl"}>
                  Connect Wallet to see royalties available to claim
                </Text>
              </Center>
              <Center mt={8}>
                <Button
                  size={"lg"}
                  bgGradient="linear(to-b, rgb(86, 170, 204) 0%, rgb(23, 21, 21) 40%)"
                  _hover={{
                    bgGradient:
                      "linear(to-b, rgb(86, 170, 204) 0%, rgb(23, 21, 21) 75%)",
                  }}
                  onClick={web3Service.toggleConnect}
                >
                  {"Connect Wallet"}
                </Button>
              </Center>
            </Container>
          )}
        </Box>
      </Center>
    </>
  );
};

export default RoyaltiesClaimer;

function RoyaltiesSection(
  title: string,
  available: string,
  total: number,
  claim: React.MouseEventHandler<HTMLButtonElement>
) {
  let formattedTotalRoyalties = total > 1000 ? nFormatter(total, 2) : total;

  return (
    <>
      <Text mt={16} fontSize={"3xl"} fontWeight={700} textAlign="center">
        {title}
      </Text>

      <SimpleGrid
        columns={{ base: 1, md: 2 }}
        spacing={{ base: 4, md: 8 }}
        my={{ base: 4, md: 8 }}
        mx={"auto"}
        maxW={"6xl"}
      >
        <Stat
          bg={useColorModeValue(
            "rgba(247, 250, 252, 0.5)",
            "rgba(23, 25, 35, 0.5)"
          )}
          rounded={"xl"}
          boxShadow={"md"}
          maxWidth={480}
          p={8}
        >
          <StatLabel>Available to claim</StatLabel>
          <StatNumber>{available}</StatNumber>
        </Stat>

        <Stat
          bg={useColorModeValue(
            "rgba(247, 250, 252, 0.5)",
            "rgba(23, 25, 35, 0.5)"
          )}
          rounded={"xl"}
          boxShadow={"md"}
          maxWidth={480}
          p={8}
        >
          <StatLabel>Total Distributed</StatLabel>
          <StatNumber>{formattedTotalRoyalties} CRO</StatNumber>
        </Stat>

        {/* <Stat bg={useColorModeValue(
                "rgba(247, 250, 252, 0.5)",
                "rgba(23, 25, 35, 0.5)"
            )}
                rounded={"xl"}
                boxShadow={"md"}
                maxWidth={480}
                p={8}>
                <StatLabel>Owned</StatLabel>
                <StatNumber>{royalties?.tokensOfWallet.length ?? '-'}</StatNumber>
            </Stat> */}
      </SimpleGrid>

      <Center mb={8}>
        <Button
          size={"lg"}
          bgGradient="linear(to-b, rgb(86, 170, 204) 0%, rgb(23, 21, 21) 40%)"
          _disabled={{
            bgGradient:
              "linear(to-b, rgb(200, 200, 200) 0%, rgb(120, 120, 120) 75%)",
          }}
          _hover={{
            bgGradient:
              "linear(to-b, rgb(86, 170, 204) 0%, rgb(23, 21, 21) 75%)",
          }}
          onClick={claim}
        >
          {`Claim ${available}`}
        </Button>
      </Center>
    </>
  );
}
